import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import { cn } from "@/lib/utils";
import { MapPin } from "lucide-react";
import { classifySegments, type RoutePing } from "./live-map";
import { PinContextMenu, type PinMenuState } from "./pin-context-menu";
import { BASEMAPS, BasemapSwitcher, type BasemapId } from "./basemap-switcher";

/** Live vehicle icon, kept in lockstep with the one in live-map.tsx (same
 * silhouette set) — duplicated rather than imported since live-map.tsx
 * doesn't export it and the replay marker has different sizing needs. */
const VEHICLE_ICON_PATHS: Record<string, string> = {
  bike: '<circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17l4-9h4l3 5"/><path d="M10 8h3"/><path d="M14 13h5"/>',
  car: '<path d="M4 16V11l2-4h12l2 4v5"/><path d="M4 16h16"/><circle cx="7.5" cy="17.5" r="1.7"/><circle cx="16.5" cy="17.5" r="1.7"/>',
  bus: '<rect x="4" y="6" width="16" height="11" rx="1.5"/><path d="M4 12h16"/><path d="M7 9h.01M12 9h.01M17 9h.01"/><circle cx="7.5" cy="19" r="1.4"/><circle cx="16.5" cy="19" r="1.4"/>',
  truck: '<path d="M3 8h10v8H3z"/><path d="M13 11h4l3 3v2h-7z"/><circle cx="6.5" cy="18" r="1.6"/><circle cx="16.5" cy="18" r="1.6"/>',
};
function vehicleIconPath(vehicleType?: string) {
  const n = (vehicleType ?? "").toLowerCase();
  if (n.includes("bike") || n.includes("moto") || n.includes("cycle")) return VEHICLE_ICON_PATHS.bike;
  if (n.includes("car")) return VEHICLE_ICON_PATHS.car;
  if (n.includes("bus") || n.includes("van")) return VEHICLE_ICON_PATHS.bus;
  if (n.includes("truck")) return VEHICLE_ICON_PATHS.truck;
  return VEHICLE_ICON_PATHS.car;
}

export type ReplayPosition = { lat: number; lng: number } | null;

/**
 * Replay map for a fixed GPS history: draws the full route once (colored by
 * moving/stopped, same logic as LiveMap) and then moves a single marker via
 * imperative `setLatLng` as `cursor` changes.
 *
 * Deliberately NOT built on LiveMap — that component's effect tears down and
 * recreates the whole Leaflet map (tiles included) on every prop change,
 * which is fine for occasional live updates but would thrash on every
 * animation tick during replay. This creates the map once and only ever
 * mutates the marker position afterward.
 */
export type ReplayMapHandle = {
  /** Drop a pin for a searched place and pan (not fit/zoom-reset) to it. */
  goToSearchResult: (lat: number, lng: number, label?: string) => void;
};

export const ReplayMap = forwardRef<ReplayMapHandle, {
  history: RoutePing[];
  cursor: ReplayPosition;
  vehicleType?: string;
  className?: string;
}>(function ReplayMap({
  history,
  cursor,
  vehicleType,
  className,
}, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const leafletRef = useRef<any>(null);
  const searchMarkerRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const labelsLayerRefs = useRef<any[]>([]);
  const [pinMenu, setPinMenu] = useState<PinMenuState>(null);
  const [basemap, setBasemap] = useState<BasemapId>("osm");
  const [mapReady, setMapReady] = useState(false);

  const removeSearchPin = () => {
    const map = mapRef.current;
    if (searchMarkerRef.current && map) map.removeLayer(searchMarkerRef.current);
    searchMarkerRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    goToSearchResult(lat: number, lng: number, label?: string) {
      const map = mapRef.current;
      const L = leafletRef.current;
      if (!map || !L) return;
      if (searchMarkerRef.current) map.removeLayer(searchMarkerRef.current);
      const icon = L.divIcon({
        className: "",
        html: '<div style="width:16px;height:16px;border-radius:9999px;background:#f97316;box-shadow:0 0 0 4px rgba(249,115,22,0.25);border:2px solid white"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const marker = L.marker([lat, lng], { icon, zIndexOffset: 900 })
        .addTo(map)
        .bindTooltip(label ?? "Searched location")
        .openTooltip();
      marker.on("contextmenu", (e: any) => {
        L.DomEvent.preventDefault(e);
        setPinMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, lat, lng, label });
      });
      searchMarkerRef.current = marker;
      map.panTo([lat, lng]);
    },
  }), []);

  // Mount: build the map + static route once per `history` identity.
  useEffect(() => {
    if (!containerRef.current || history.length === 0) return;
    let cancelled = false;

    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current) return;
      leafletRef.current = L;

      // @ts-expect-error - internal
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
      mapRef.current = map;
      map.on("movestart zoomstart", () => setPinMenu(null));
      tileLayerRef.current = L.tileLayer(BASEMAPS[basemap].url, {
        maxZoom: BASEMAPS[basemap].maxZoom,
        attribution: BASEMAPS[basemap].attribution,
      }).addTo(map);
      labelsLayerRefs.current = (BASEMAPS[basemap].labelsUrls ?? []).map((url) =>
        L.tileLayer(url, { maxZoom: BASEMAPS[basemap].maxZoom }).addTo(map),
      );

      if (history.length >= 2) {
        const segments = classifySegments(history);
        for (let k = 0; k < history.length - 1; k++) {
          const a = history[k];
          const b = history[k + 1];
          L.polyline(
            [
              [a.lat, a.lng],
              [b.lat, b.lng],
            ],
            { color: segments[k] === "stopped" ? "#a855f7" : "#3D98FF", weight: 4, opacity: 0.85 },
          ).addTo(map);
        }
      }

      const startIcon = L.divIcon({
        className: "",
        html: '<div style="width:14px;height:14px;border-radius:9999px;background:oklch(0.676 0.175 253);box-shadow:0 0 0 4px oklch(0.676 0.175 253 / 0.25);border:2px solid white"></div>',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      const endIcon = L.divIcon({
        className: "",
        html: '<div style="width:16px;height:16px;border-radius:9999px;background:oklch(0.60 0.135 155);box-shadow:0 0 0 4px oklch(0.60 0.135 155 / 0.25);border:2px solid white"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      L.marker([history[0].lat, history[0].lng], { icon: startIcon }).addTo(map).bindTooltip("Start of range");
      L.marker([history[history.length - 1].lat, history[history.length - 1].lng], { icon: endIcon })
        .addTo(map)
        .bindTooltip("End of range");

      const vehicleIcon = L.divIcon({
        className: "",
        html: `<div style="width:30px;height:30px;border-radius:9999px;background:oklch(0.72 0.15 140);box-shadow:0 0 0 6px oklch(0.72 0.15 140 / 0.3);border:2px solid white;display:flex;align-items:center;justify-content:center">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${vehicleIconPath(vehicleType)}</svg>
        </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      const initial = cursor ?? history[0];
      markerRef.current = L.marker([initial.lat, initial.lng], { icon: vehicleIcon, zIndexOffset: 1000 }).addTo(map);

      const bounds: [number, number][] = history.map((p) => [p.lat, p.lng]);
      map.fitBounds(bounds, { padding: [40, 40] });
      setMapReady(true);
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
      searchMarkerRef.current = null;
      tileLayerRef.current = null;
      labelsLayerRefs.current = [];
      setMapReady(false);
    };
    // Rebuild only when the underlying dataset changes, not on every cursor tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Cursor moves: imperative marker update only, no map rebuild.
  useEffect(() => {
    if (!cursor || !markerRef.current) return;
    markerRef.current.setLatLng([cursor.lat, cursor.lng]);
  }, [cursor]);

  // Swap tile layer (and its labels overlays, if any) in place when the user
  // picks a different basemap.
  useEffect(() => {
    const map = mapRef.current;
    const L = leafletRef.current;
    if (!mapReady || !map || !L) return;
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    labelsLayerRefs.current.forEach((layer) => map.removeLayer(layer));
    tileLayerRef.current = L.tileLayer(BASEMAPS[basemap].url, {
      maxZoom: BASEMAPS[basemap].maxZoom,
      attribution: BASEMAPS[basemap].attribution,
    }).addTo(map);
    tileLayerRef.current.bringToBack();
    labelsLayerRefs.current = (BASEMAPS[basemap].labelsUrls ?? []).map((url) =>
      L.tileLayer(url, { maxZoom: BASEMAPS[basemap].maxZoom }).addTo(map),
    );
  }, [basemap, mapReady]);

  if (history.length === 0) {
    return (
      <div className={cn("relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border bg-surface-muted p-6 text-center", className)}>
        <MapPin className="h-6 w-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No GPS pings in the selected range.</p>
      </div>
    );
  }

  return (
    <div className={cn("relative isolate overflow-hidden rounded-xl border bg-surface-muted", className)}>
      <div ref={containerRef} className="h-full min-h-64 w-full" />
      <BasemapSwitcher value={basemap} onChange={setBasemap} />
      <PinContextMenu menu={pinMenu} onClose={() => setPinMenu(null)} onRemove={removeSearchPin} />
    </div>
  );
});

/** Linear interpolation between the two pings bracketing `atMs` (epoch ms).
 * Physically-consistent regardless of ping density: a 5s-apart pair and a
 * 5min-apart pair both move the marker proportionally to elapsed real time,
 * not proportionally to ping count. Clamps to the first/last ping outside range. */
export function interpolatePosition(history: RoutePing[], atMs: number): ReplayPosition {
  if (history.length === 0) return null;
  if (history.length === 1) return { lat: history[0].lat, lng: history[0].lng };

  const first = new Date(history[0].recorded_at).getTime();
  const last = new Date(history[history.length - 1].recorded_at).getTime();
  if (atMs <= first) return { lat: history[0].lat, lng: history[0].lng };
  if (atMs >= last) return { lat: history[history.length - 1].lat, lng: history[history.length - 1].lng };

  // Pings are sorted ascending (backend guarantees ORDER BY recorded_at ASC) — binary search.
  let lo = 0;
  let hi = history.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (new Date(history[mid].recorded_at).getTime() <= atMs) lo = mid;
    else hi = mid;
  }
  const a = history[lo];
  const b = history[hi];
  const t0 = new Date(a.recorded_at).getTime();
  const t1 = new Date(b.recorded_at).getTime();
  const frac = t1 > t0 ? (atMs - t0) / (t1 - t0) : 0;
  return { lat: a.lat + (b.lat - a.lat) * frac, lng: a.lng + (b.lng - a.lng) * frac };
}
